import { Avatar, EnumValue, TableColumnType, Table } from '@/components';
import { useGetUsersQuery, UserState } from '@/redux';
import { Space, Typography, Button } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { UserStatusBadge } from './UserStatusBadge';
import { Icon } from '@/components';
import { Role } from '@/core';
import classes from './UsersListPanel.module.scss';
import { useExcelExport } from '@/components/Table/useExcelExport';

function useColumns() {
  const { t } = useTranslation();

  return useMemo<TableColumnType<UserState>[]>(
    () => [
      {
        title: t('users.name'),
        key: 'name',
        render: (_, { firstName, lastName, avatar, id }) => (
          <Space size="middle">
            <Avatar size={32} reference={avatar} alt={`${firstName} ${lastName}`} />
            <Typography.Text strong>
              <NavLink to={`/users/${id}`}>{`${firstName} ${lastName}`}</NavLink>
            </Typography.Text>
          </Space>
        ),
        width: '28%',
      },
      {
        title: t('users.email'),
        defaultSortOrder: 'ascend',
        key: 'email',
        dataIndex: 'email',
        render: (_, { email }) => <Typography.Text strong>{email}</Typography.Text>,
        width: '28%',
      },
      {
        title: t('users.role'),
        key: 'role',
        render: (_, { role }) => (
          <Typography.Text strong>
            <EnumValue type={Role} value={role} />
          </Typography.Text>
        ),
        width: '28%',
      },
      {
        title: t('users.status.title'),
        key: 'status',
        render: (_, user) => <UserStatusBadge user={user} />,
        width: '16%',
      },
    ],
    [t],
  );
}

type UsersListPanelType = {
  handleAddUser: () => void;
};

export function UsersListPanel({ handleAddUser }: UsersListPanelType) {
  const { t } = useTranslation();
  const columns = useColumns();
  const { data: users, isFetching } = useGetUsersQuery();
  const [visibleData, setVisibleData] = useState<readonly UserState[]>([]);
  const { exportAction } = useExcelExport(columns, visibleData);

  return (
    <div className={classes.usersListPanel}>
      <div className={classes.usersListPanelControls}>
        <Button size="large" type="primary" onClick={handleAddUser} icon={<Icon type="add" />}>
          {t('users.add.btn')}
        </Button>
        {exportAction}
      </div>
      <Table<UserState>
        loading={isFetching}
        visibleData={visibleData}
        setVisibleData={setVisibleData}
        columns={columns}
        dataSource={users}
        rowKey={(row) => row.id}
      />
    </div>
  );
}
